// Everyone a player can meet, one fixed key each (studio/ACHIEVEMENTS-PLAN.md): `group:id`, never renamed. The look at the glass and the
// scream at a wrong Open come from here for the client (client/visitors.js makeLook) and for the server, which records who was met from
// the round's own id, so the two always agree and the client never claims a meeting. Cosmetic only: no points, no prizes, no odds shown.
import { ANIMALS, animalOf, animalMixOf } from './animals.js';
import { DEGENS, degenOf } from './degens.js';
import { REGULARS, regularOf } from './regulars.js';
import { FAILS, failOf, PAIRED } from './fails.js';
import { GAMES } from './minigames/registry.js';
import { glitchOf } from './glitch.js';

// the look's seed: a round's id (or a challenge's face) to a number, FNV-1a
export const seedOf = s => {
  let h = 2166136261;
  for (const c of String(s)) { h ^= c.charCodeAt(0); h = Math.imul(h, 16777619); }
  return h >>> 0;
};

// the drawn head a visitor wears, or null for a person. animal: a Full fever door, every head an animal. degen: the normal lineup, where
// about one door in four is an animal (game/animals.js ANIMAL_SHARE), else a degen visitor, else a drawn neighbour. All by seed alone.
export const headOf = (seed, { animal = false, degen = false } = {}) => (animal ? animalOf(seed) : degen ? (animalMixOf(seed) ?? degenOf(seed) ?? regularOf(seed)) : null);
// the logbook group of a drawn head
export const groupOf = head => (ANIMALS.includes(head) ? 'animal' : DEGENS.includes(head) ? 'degen' : 'regular');
// who screams at a wrong Open on an alien: a calm neighbour's own monster, else the seed's pick; null is the Knocker
export const failFor = (seed, head) => PAIRED[head] ?? failOf(seed);

const NAMES = {
  animal: { pigeon: 'Gerald the pigeon', cowboy: 'The cowboy dog', afrocat: 'The afro cat', hamster: 'The hamster in headphones', frog: 'The frog in a crown',
    dog: 'The bulldog in shades', goat: 'The goat in a Roman helmet', capy: 'The capybara with a towel', owl: 'The owl in a nightcap', raccoon: 'The raccoon with a torch' },
  degen: { whale: 'The whale', laser: 'Laser eyes', influencer: 'The influencer', anon: 'The anon dev', frens: 'Two frens in a coat', balloon: 'The hopium balloon',
    ice: 'Cold storage', farm: 'The bot farm', rocket: 'The rocket' },
  regular: { 'n-eyes': 'Many-Eyes', 'n-moth': 'The moth in the lampshade', 'n-set': 'The set', 'n-spider': 'The spider in the bucket hat', goblin: 'The goblin',
    'brain-cap': 'The brain in a cap', 'brain-horns': 'The brain in horns', copfrog: 'The cop frog', bouffant: 'The bouffant', hood: 'The hood', rasta: 'The rasta bird',
    skull: 'The skull in earmuffs' },
  fail: { knocker: 'The Knocker', eyes: 'The eyes', moth: 'The moth', set: 'The eye in the set', spider: 'The spider' },
  knock: { gold: 'A gold knock', red: 'A red knock', bull: 'The bull', bear: 'The bear', outbreak: 'An outbreak' },
  guest: { holden: 'Mr Holden' },
  glitch: { toy: 'Toy town' },
};
// a stamp for meeting everyone in a group (Mr Holden and the glitch, groups of one, have none): kept in the logbook only, never on a board or in a payout
export const STAMPS = { regular: 'Every neighbour', degen: 'Degen of the door', animal: 'Full fever', fail: 'Every scream', knock: 'Every knock', story: 'The whole block', mini: 'Every game' };
export const GROUPS = [
  { group: 'regular', title: 'The neighbours', ids: REGULARS },
  { group: 'degen', title: 'The degens', ids: DEGENS },
  { group: 'animal', title: 'Animal fever', ids: ANIMALS },
  { group: 'fail', title: 'Every scream', ids: ['knocker', ...FAILS] },
  { group: 'knock', title: 'The knocks', ids: Object.keys(NAMES.knock) },
  { group: 'guest', title: 'Guests', ids: Object.keys(NAMES.guest) },
  { group: 'glitch', title: 'The glitch', ids: Object.keys(NAMES.glitch) },
  { group: 'story', title: 'The whole block', ids: null },
  { group: 'mini', title: 'The games', ids: Object.keys(GAMES) },
];
// the groups with their keys and names. story: { id: name } of the story's cast, which only the server has (game/story.js stays off the
// page: it says who is a visitor), so the client gets the whole logbook from the server
export function cast(story = {}) {
  return GROUPS.map(g => {
    const ids = g.ids ?? Object.keys(story);
    const name = id => (g.group === 'story' ? story[id] : g.group === 'mini' ? GAMES[id].NAME : NAMES[g.group][id]);
    return { ...g, entries: ids.map(id => ({ key: `${g.group}:${id}`, name: name(id) })) };
  });
}
export const KEYS = (story = {}) => cast(story).flatMap(g => g.entries.map(e => e.key));
export const nameOfKey = (key, story = {}) => cast(story).flatMap(g => g.entries).find(e => e.key === key)?.name ?? null;

// Who a doorkeeper met at a finished door: night-shift doors, and practice doors only until the night shift opens (the owner's call);
// story doors and the games have their own.
// roundId: the round's id, the look's seed; fever: a Full fever door (game/animals.js nextFever, kept by the server); kind: the revealed kind; look: a guest's look;
// plain: a lesson's door or the Big Knock's, which never glitch (game/glitch.js).
export function metOnDoor({ roundId, fever = false, room, special = null, outbreak = false, kind, verdict, look = null, nightOpen = true, plain = false }) {
  if (room !== 'night' && !(room === 'practice' && !nightOpen)) return [];
  const seed = seedOf(roundId), keys = [];
  if (look === 'holden') keys.push('guest:holden');
  else if (special !== 'bull' && special !== 'bear') { // a bull or a bear knocks as itself, not in a drawn head
    const head = headOf(seed, { animal: fever, degen: true });
    if (head) keys.push(`${groupOf(head)}:${head}`); // a mixed-in animal and a fever animal both go to the animal group
  }
  if (special && NAMES.knock[special]) keys.push(`knock:${special}`);
  if (outbreak) keys.push('knock:outbreak');
  if (!plain && glitchOf(seed)) keys.push('glitch:toy'); // the toy town showed at this door
  // the scream: a wrong Open on an alien, on a knock that floods (bull and bear have their own reveal; call-lands.js)
  if (verdict === 'open' && kind === 'alien' && special !== 'bull' && special !== 'bear') {
    const head = look === 'holden' ? null : headOf(seed, { animal: fever, degen: true });
    keys.push(`fail:${failFor(seed, head) ?? 'knocker'}`);
  }
  return keys;
}
